import { useState, useEffect, useCallback } from 'react';
import { useLazyGetNotificationsQuery, useMarkAsReadMutation } from '../../../api/service';

export function useNotifications(userId) {
	const [notifications, setNotifications] = useState([]);
	const [hasMore, setHasMore] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const [unopenedCount, setUnopenedCount] = useState(0);
	const [totalNotifications, setTotalNotifications] = useState(0);
	const [resetState, setResetState] = useState(false); // Flag to manage reset logic

	const [triggerGetNotifications, { isFetching }] = useLazyGetNotificationsQuery();
	const [markAsRead] = useMarkAsReadMutation();

	const PAGE_SIZE = 10; // Default page size from API

	// Fetch notifications for a specific page
	const fetchNotifications = useCallback(
		async (page = 1) => {
			if (!userId) return;
			try {
				const response = await triggerGetNotifications({ userId, page }).unwrap();
				setNotifications((prev) => {
					const newNotifications = response.notifications;
					const notificationMap = new Map();

					// Add existing notifications to the map
					prev.forEach((notif) => {
						notificationMap.set(notif.ID, notif);
					});

					// Add or replace with new notifications
					newNotifications.forEach((notif) => {
						notificationMap.set(notif.ID, notif);
					});

					// Return updated array of unique notifications
					return Array.from(notificationMap.values());
				});

				setUnopenedCount(response.unopenedCount);
				setTotalNotifications(response.total);

				// Compute `hasMore` based on total and fetched notifications
				const totalFetched = page * PAGE_SIZE;
				setHasMore(totalFetched < response.total);

				setCurrentPage(page);
				return response;
			} catch (error) {
				console.error('Error fetching notifications:', error);
			}
		},
		[triggerGetNotifications, userId]
	);

	// Fetch the first page of notifications when the component mounts
	useEffect(() => {
		if (userId) {
			fetchNotifications(1);
		}
	}, [userId, fetchNotifications]);

	const reset = useCallback(() => {
		setResetState(true);
		setNotifications([]);
		setHasMore(true);
		setCurrentPage(1);
	}, []);

	// Fetch the next page of notifications
	const fetchNextPage = useCallback(() => {
		if (resetState) {
			setResetState(false); // Reset the flag to allow future fetches
			return;
		}

		if (hasMore && !isFetching) {
			fetchNotifications(currentPage + 1);
		}
	}, [hasMore, isFetching, currentPage, fetchNotifications]);

	const handleMarkAsRead = async (id) => {
		try {
			await markAsRead({ userId, notificationId: id }).unwrap();
			await fetchNotifications(currentPage);
		} catch (error) {
			console.log(error);
			console.error('Eroare de server: notificările nu au putut fi găsite');
		}
	};

	return {
		notifications,
		unopenedCount,
		hasMore,
		isFetching,
		handleMarkAsRead,
		fetchNextPage,
		refetch: () => fetchNotifications(1), // Helper to refetch from the first page
		reset // Reset state except unopenedCount
	};
}
